import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './api-error.js';
import { sortQuerySchema } from './sort.js';
import { checkStatusSchema } from './check.js';
import { mailProviderSchema } from './provider.js';

/**
 * Slug pattern shared by clients, forms and checks: lowercase kebab-case, starting
 * with a letter.
 *
 * @since 1.0.0
 */
export const SLUG_PATTERN = /^[a-z][a-z0-9]*(-[a-z0-9]+)*$/;

export const slugSchema = z.string().min(1).max(64).regex(SLUG_PATTERN, 'Must be lowercase kebab-case');

/**
 * Longest sending domain a client may register, the DNS limit for a full host name.
 *
 * @since 1.3.0
 */
export const SENDING_DOMAIN_MAX_LENGTH = 253;

/**
 * Longest IANA time zone name a client may carry.
 *
 * @since 1.3.0
 */
export const TIMEZONE_MAX_LENGTH = 64;

/**
 * What a create route returns: the new row id and, where the entity has one, its slug.
 *
 * @since 1.3.0
 */
export const createdIdSchema = z.object({
    id: z.number().int(),
    slug: slugSchema.optional(),
});

export type CreatedId = z.infer<typeof createdIdSchema>;

/**
 * What creating a client returns: the row id and the slug the dialog links to next.
 *
 * @since 1.3.0
 */
export const createdClientSchema = createdIdSchema.required({ slug: true });

export type CreatedClient = z.infer<typeof createdClientSchema>;

/**
 * How far a client has progressed through onboarding. Drives the Setup pill on the
 * clients view.
 *
 * @since 1.0.0
 */
export const ONBOARD_STATES = ['pending', 'partial', 'live'] as const;

export const onboardStateSchema = z.enum(ONBOARD_STATES);

export type OnboardState = z.infer<typeof onboardStateSchema>;

export const clientSchema = z.object({
    id: z.number().int(),
    slug: slugSchema,
    name: z.string(),
    sendingDomain: z.string().min(1).max(SENDING_DOMAIN_MAX_LENGTH),
    siteUrl: z.url(),
    providerAccountId: z.number().int(),
    checklyTag: z.string(),
    timezone: z.string().min(1).max(TIMEZONE_MAX_LENGTH),
    onboardState: onboardStateSchema,
    settings: z.record(z.string(), z.unknown()),
    createdAt: z.number().int(),
    archivedAt: z.number().int().nullable(),
});

export type Client = z.infer<typeof clientSchema>;

/**
 * Shortest site key a monitored site can generate.
 *
 * @since 1.3.0
 */
export const QC_SITE_KEY_MIN_LENGTH = 32;

/**
 * Longest site key a monitored site can generate.
 *
 * @since 1.3.0
 */
export const QC_SITE_KEY_MAX_LENGTH = 128;

/**
 * The key a monitored site generates for itself and displays on its settings screen.
 * It is written to the client record and never read back out through the API.
 *
 * @since 1.0.0
 */
export const qcSiteKeySchema = z
    .string()
    .min(QC_SITE_KEY_MIN_LENGTH)
    .max(QC_SITE_KEY_MAX_LENGTH)
    .regex(/^[A-Za-z0-9]+$/, 'Site keys are alphanumeric');

export const createClientRequestSchema = z.object({
    slug: slugSchema,
    name: z.string().min(1).max(160),
    sendingDomain: z.string().min(4).max(SENDING_DOMAIN_MAX_LENGTH),
    siteUrl: z.url({ protocol: /^https$/ }),
    providerAccountId: z.number().int().optional(),
    // the worker fills a missing timezone from its DEFAULT_TIMEZONE var
    timezone: z.string().min(1).max(TIMEZONE_MAX_LENGTH).optional(),
    qcSiteKey: qcSiteKeySchema.optional(),
});

export type CreateClientRequest = z.infer<typeof createClientRequestSchema>;

export const updateClientRequestSchema = createClientRequestSchema.partial().omit({ slug: true });

export type UpdateClientRequest = z.infer<typeof updateClientRequestSchema>;

/**
 * One step of the four-step onboarding flow shown on the clients view.
 *
 * @since 1.0.0
 */
export const onboardingStepSchema = z.object({
    key: z.enum(['sending-domain', 'webhooks', 'qc-mode', 'forms']),
    label: z.string(),
    detail: z.string(),
    isComplete: z.boolean(),
    blocker: z.string().nullable(),
});

export type OnboardingStep = z.infer<typeof onboardingStepSchema>;

/**
 * The onboarding steps for one client and the state they add up to.
 *
 * @since 1.3.0
 */
export const clientOnboardingResponseSchema = z.object({
    steps: z.array(onboardingStepSchema),
    onboardState: onboardStateSchema,
});

export type ClientOnboardingResponse = z.infer<typeof clientOnboardingResponseSchema>;

/**
 * A row of the client roster: the record's public fields plus the form count and the
 * latest status of each overview check, so the view never has to fetch more.
 *
 * @since 1.3.0
 */
export const clientRosterRowSchema = clientSchema
    .pick({ slug: true, name: true, sendingDomain: true, siteUrl: true, onboardState: true })
    .extend({
        hasSiteKey: z.boolean(),
        formCount: z.number().int().nonnegative(),
        checkStatuses: z.array(checkStatusSchema),
    });

export type ClientRosterRow = z.infer<typeof clientRosterRowSchema>;

/**
 * One page of the client roster.
 *
 * @since 1.3.0
 */
export const clientRosterPageSchema = paginatedSchema(clientRosterRowSchema);

export type ClientRosterPage = z.infer<typeof clientRosterPageSchema>;

/**
 * Columns the client roster may be sorted on, named after the table's own column keys.
 * The check dots and the setup state are derived after the page is read, so neither is
 * offered here.
 *
 * @since 1.4.0
 */
export const CLIENT_SORT_KEYS = ['name', 'domain', 'site', 'forms'] as const;

export const clientSortKeySchema = z.enum(CLIENT_SORT_KEYS);

export type ClientSortKey = z.infer<typeof clientSortKeySchema>;

export const clientRosterQuerySchema = paginationQuerySchema.extend(
    sortQuerySchema(CLIENT_SORT_KEYS, 'name', 'asc').shape,
);

export type ClientRosterQuery = z.infer<typeof clientRosterQuerySchema>;

/**
 * One client as the detail endpoint returns it: the stored record minus what the API
 * keeps to itself. The site key is write-only, so only whether one is set comes back.
 *
 * @since 1.3.0
 */
export const clientDetailSchema = clientSchema
    .pick({
        slug: true,
        name: true,
        sendingDomain: true,
        siteUrl: true,
        providerAccountId: true,
        timezone: true,
        onboardState: true,
        createdAt: true,
    })
    .extend({
        hasSiteKey: z.boolean(),
    });

export type ClientDetail = z.infer<typeof clientDetailSchema>;

/**
 * How the client detail route wraps its record.
 *
 * @since 1.3.0
 */
export const clientDetailEnvelopeSchema = z.object({
    client: clientDetailSchema,
});

export type ClientDetailEnvelope = z.infer<typeof clientDetailEnvelopeSchema>;

/**
 * What re-pointing a client's provider webhooks at this dashboard created.
 *
 * The resources are named so an operator can find them in the provider's console:
 * provisioning is idempotent, so a repeat run reports the same names.
 *
 * @since 1.0.0
 */
export const provisionWebhooksResultSchema = z.object({
    provider: mailProviderSchema,
    domain: z.string(),
    target: z.string(),
    resources: z.array(z.string()),
});

export type ProvisionWebhooksResult = z.infer<typeof provisionWebhooksResultSchema>;
